import './WidgetQuantitativeChart.css';

import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { calculateMinMaxDatesFromDatesArray, calculateMinMaxValuesFromValuesArray, calculateObjectiveSegmentValues, getStatusColor } from '../../../../../b2b-plans/components/charting/helper';
import { generateClassName, getIsoDate } from '../../../../../b2b-common/helpers';

function getLastNSamples(samplesData, n) {
    const sortedSamples = samplesData.sort((a, b) => new Date(b.value[0]) - new Date(a.value[0]));
    const lastNSamples = sortedSamples.slice(0, n);
    return lastNSamples;
}

function getObjective(objective) {
    // console.log('objective', objective);
    const series = [];
    if (objective.objectiveType === 'Regular') {
        series.push({
            name: 'Quantitative Objective',
            data: [
                [getIsoDate(objective.startDate), objective.startValue],
                [getIsoDate(objective.endDate), objective.endValue]
            ],
            type: 'line',
            lineStyle: {
                type: 'dotted',
                color: '#3233c1'
            },
            itemStyle: {
                color: '#3233c1'
            },
            symbol: 'diamond',
            symbolSize: 10,
        });
    } else if (objective.objectiveType === 'Threshold_Above' || objective.objectiveType === 'Threshold_Below') {
        series.push({
            name: 'Quantitative Objective Threshold',
            data: [
                [getIsoDate(objective.startDate), objective.startValue],
                [getIsoDate(objective.endDate), objective.startValue]
            ],
            type: 'line',
            lineStyle: {
                type: 'dotted',
                color: '#3233c1'
            },
            itemStyle: {
                color: '#3233c1'
            },
            symbol: 'diamond',
            symbolSize: 10,
        });
    } else if (objective.objectiveType === 'Range') {
        series.push({
            name: 'Quantitative Objective Lower Range',
            data: [
                [getIsoDate(objective.startDate), objective.startValue],
                [getIsoDate(objective.endDate), objective.startValue]
            ],
            type: 'line',
            lineStyle: {
                type: 'dotted',
                color: '#3233c1'
            },
            itemStyle: {
                color: '#3233c1'
            },
            symbol: 'diamond',
            symbolSize: 10,
        });
        series.push({
            name: 'Quantitative Objective Upper Range',
            data: [
                [getIsoDate(objective.startDate), objective.endValue],
                [getIsoDate(objective.endDate), objective.endValue]
            ],
            type: 'line',
            lineStyle: {
                type: 'dotted',
                color: '#3233c1'
            },
            itemStyle: {
                color: '#3233c1'
            },
            symbol: 'diamond',
            symbolSize: 10,
        });
    }
    // console.log('series', series);
    return series;
}

const getSamplesAndObjective = (objective) => {
    const samplesData = objective.samplesPerObjective.map(sample => ({
        value: [getIsoDate(sample.sampleDate), sample.quantitativeValue],
        status: sample.status,
        trend: sample.trend
    }));
    const last10Samples = getLastNSamples(samplesData, 10);
    const maxMinDates = calculateMinMaxDatesFromDatesArray(last10Samples.map(sample => new Date(sample.value[0])));
    const objectiveSegment = calculateObjectiveSegmentValues(objective, maxMinDates.minDate, maxMinDates.maxDate);
    const valuesArray = last10Samples.map(sample => sample.value[1]);
    if ((objectiveSegment.objectiveType === 'Regular') || (objectiveSegment.objectiveType === 'Range')) {
        valuesArray.push(objectiveSegment.startValue);
        valuesArray.push(objectiveSegment.endValue);
    } else {
        valuesArray.push(objectiveSegment.startValue);
    }
    const minMaxValues = calculateMinMaxValuesFromValuesArray(valuesArray, 0.1);
    return { last10Samples, minMaxValues, objectiveSegment };
};

const getOptions = (
    last10Samples,
    objectiveSegment,
    minMaxValues,
    showX = true,
    showY = true,
    grid = { left: '3%', right: '3%', top: '7%', bottom: '5%', containLabel: true }
) => {
    const options = {
        grid,
        xAxis: {
            type: 'time',
            show: true,
            axisLabel: {
                show: false
            },
            axisTick: {
                show: showX
            }
        },
        yAxis: {
            type: 'value',
            show: true,
            min: minMaxValues.minValue,
            max: minMaxValues.maxValue,
            axisLabel: {
                show: false
            },
            axisTick: {
                show: showY
            },
            axisLine: {
                show: true // This ensures the Y axis line is visible
            },
        },
        tooltip: {
            trigger: 'item',
            formatter: function (params) {
                const date = new Date(params.value[0]).toLocaleDateString();
                const value = params.value[1];
                return `Date: ${date}<br/>Value: ${value}`;
            }
        },
        series: [
            {
                name: 'Last 10 Samples',
                data: last10Samples,
                type: 'line',
                smooth: true,
                symbol: 'circle',
                symbolSize: 7,
                itemStyle: {
                    color: function (params) {
                        return getStatusColor(params.data.status);
                    }
                },
                lineStyle: {
                    type: 'solid',
                    color: '#007aff',
                    width: 1
                },
            },
            ...getObjective(objectiveSegment),
        ]
    };
    return options;
}


const WidgetQuantitativeChart = ({ objective, showX = true, showY = true,
    grid = { left: '3%', right: '3%', top: '7%', bottom: '5%', containLabel: true },
    className = null }) => {

    const [currentObjective, setCurrentObjective] = useState(objective);
    const { last10Samples, minMaxValues, objectiveSegment } = getSamplesAndObjective(objective);
    const [options, setOptions] = useState(getOptions(last10Samples, objectiveSegment, minMaxValues, showX, showY, grid));

    useEffect(() => {
        if (!objective)
            return;
        else if (objective.objectiveId === currentObjective.objectiveId)
            return;
        setCurrentObjective(objective);
        const { last10Samples, minMaxValues, objectiveSegment } = getSamplesAndObjective(objective);
        setOptions(getOptions(last10Samples, objectiveSegment, minMaxValues));
    }, [objective]);

    // console.log('rendering chart with options', options);

    return (
        <div className={generateClassName('widget-quantitative-chart-container','',className)} style={{ height: '100%', width: '100%' }}>
            <ReactECharts
                option={options}
                className='widget-quantitative-echart'
                style={{ height: '100%', width: '100%' }}
                // opts={{ renderer: 'canvas', width: 'auto', height: 'auto' }}
            />
        </div>
    );
};

export default WidgetQuantitativeChart;